import { useEffect, useRef, useState } from "react";
import { VideoTile } from "./VideoTile";
import { RailTiles } from "./RailTiles";
import { AnnotationToolbar } from "@vc/views/panels/AnnotationToolbar";
import type { Participant } from "@vc/models";
import type { BackgroundImage } from "@vc/data/backgroundCategories";
import { FileText, Maximize2, Minimize2, PenLine, X } from "lucide-react";
import { useTranslation } from "@/clientWeb/i18n/LanguageContext";
import { useFullscreen } from "@vc/viewmodels/screen/useFullscreen";
import { usePlayableUrl } from "@/features/meeting/hooks/usePlayableUrl";
import { isUploadedShareUrl, downloadSharedFile } from "@/features/meeting/services/share-upload";
import { useSharedTextContent, isTextualShare, parseCsv } from "@/features/meeting/hooks/useSharedTextContent";
import { useSharedDocx, isDocxShare, isLegacyDocShare } from "@/features/meeting/hooks/useSharedDocx";
import { useSharedSheet, isXlsxShare, isLegacyXlsShare } from "@/features/meeting/hooks/useSharedSheet";
import { SharedPdfViewer } from "./SharedPdfViewer";
import { SharedImageViewer } from "./SharedImageViewer";
import { AnnotationLayer } from "./AnnotationLayer";
import { needsPaintedPdf } from "./pdf-support";
import { useMeetingAnnotationStore, type LiveStroke } from "@/features/meeting/store/meeting-annotation.store";
import { selectCanAnnotate, useMeetingHostControlsStore } from "@/features/meeting/store/meeting-host-controls.store";
import { useAnnotations } from "@/features/meeting/hooks/useAnnotations";
import type { AnnotationTool, ShapeTool, StampKind, Stroke } from "@/features/meeting/services/annotation-geometry";
import type { ChatChannel } from "@/features/chat/services/chat.service";
import { ScreenShareRequestDialog } from "@vc/views/dialogs/ScreenShareRequestDialog";
import { useAnnotationRequestsStore } from "@/features/meeting/store/annotation-requests.store";
import { toast } from "@/clientWeb/components/ui/toast-utils";

/** Image types the store serves inline (svg is not: it can run script). */
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'bmp', 'heic'];

const NO_STROKES: Stroke[] = [];
const NO_LIVE: Record<string, LiveStroke> = {};

/** What annotation needs from the meeting: which presentation this is, who
 *  this client is, and the room channel the ink travels on. */
export interface DocumentAnnotationContext {
  /** The presentation's id as the room knows it. */
  shareId: string;
  /** This client's peer id. */
  selfId: string | null;
  /** Host or co-host — who may always draw. */
  isModerator: boolean;
  /** Holds an annotation grant — may draw while annotation is enabled.
   *  Presenter access is not enough, even on a document they present. */
  isAnnotator: boolean;
  /** Ask the host for annotation access (`annotation:request`). */
  onRequestAnnotation?: () => void;
  channel: ChatChannel | null;
}

interface DocumentPresentationLayoutProps {
  documentName: string;
  /** Where the document lives. Absent (or a blob: URL from a local upload that
   *  was never sent anywhere) falls back to the name-only placeholder. */
  documentUrl?: string;
  /** May this viewer stop the presentation? True for whoever started it and for
   *  moderators; false for everyone else, who should not be able to yank a
   *  document off the room's stage. */
  canClose?: boolean;
  /** True when THIS client started the presentation. Drives the wording of the
   *  banner and the presenter badge — a viewer must not be told they are the
   *  one sharing. */
  isPresenter?: boolean;
  presenterName: string;
  /** Emoji for one participant, or null. Per-peer: a reaction
   *  belongs to whoever sent it, not to every tile on screen. */
  reactionFor: (peerId: string | undefined | null) => string | null;
  pinnedIds: string[];
  railRowChunks: Participant[][];
  rightRailWidth: number;
  rightRailPageCount: number;
  safeRightRailPage: number;
  railRowH: number;
  onPin: (participantId: string) => void;
  /** Moderator only: pin a tile on everyone's screen. */
  onPinForAll?: (participantId: string) => void;
  pinnedForAllId?: string | null;
  onMaximize: (element: HTMLElement) => void;
  onOpenChat: (participantId: string) => void;
  setRightRailEl: (node: HTMLDivElement | null) => void;
  /** Phone layout: no desktop participant rail — the caller shows people in a
   *  strip — and no annotation toolbar, which covered a third of a phone's
   *  document while drawing nothing. */
  phoneLayout?: boolean;
  onSetRightRailPage: (page: number | ((prev: number) => number)) => void;
  selectedBackground: BackgroundImage | null;
  onClose: () => void;
  railDragRef?: React.MutableRefObject<{ startX: number; startWidth: number } | null>;
  /** Mic and camera buttons, shown in the banner only while this layout owns
   *  full screen — the meeting's control bar is outside it and not painted. */
  fullscreenControls?: React.ReactNode;
  /** Annotation over the document. Absent: the document is shown without it. */
  annotation?: DocumentAnnotationContext;
}

export function DocumentPresentationLayout({
  documentName,
  documentUrl,
  canClose = true,
  isPresenter = true,
  presenterName,
  reactionFor,
  pinnedIds,
  railRowChunks,
  rightRailWidth,
  rightRailPageCount,
  safeRightRailPage,
  railRowH,
  onPin,
  onPinForAll,
  pinnedForAllId,
  onMaximize,
  onOpenChat,
  setRightRailEl,
  phoneLayout = false,
  onSetRightRailPage,
  selectedBackground,
  onClose,
  railDragRef,
  fullscreenControls,
  annotation,
}: DocumentPresentationLayoutProps) {
  const t = useTranslation();
  // Fullscreen targets the *main* document container so the annotation toolbar
  // stays accessible inside fullscreen; the participant rail sits outside this
  // ref and is naturally hidden.
  const mainRef = useRef<HTMLDivElement>(null);
  const { isFullscreen, exit: exitFullscreen, toggle: toggleFullscreen } = useFullscreen(mainRef);

  const handleClose = () => {
    if (isFullscreen) exitFullscreen()
    onClose()
  };


  // Uploaded documents sit behind the backend's bearer auth, which an <iframe>
  // cannot present — resolved here under this client's own identity.
  const {
    playableUrl: playableDocUrl,
    mime: docMime,
    loading: docResolving,
    error: docError,
  } = usePlayableUrl(documentUrl, documentName);

  // A text file is painted by us, not framed. See useSharedTextContent for why:
  // an iframe obeys `Content-Disposition`, and the store sets it on anything it
  // could not type — which is how sharing a CSV downloaded it on everyone's
  // machine and showed a blank frame instead of the sheet.
  const { text: sharedText, loading: textLoading } = useSharedTextContent(playableDocUrl, docMime);
  const showAsText = isTextualShare(docMime) && !!playableDocUrl;
  const csv = docMime.split(';')[0].trim().toLowerCase() === 'text/csv' && sharedText
    ? parseCsv(sharedText)
    : null;

  // A served PDF is a PDF, whatever the name says. A PowerPoint, Word or text
  // file is presented as the PDF copy the backend made of it, under the
  // original name — and judged by that name it would get the Word or Excel
  // treatment below, which reflows and has no annotation.
  const servedPdf = docMime.toLowerCase().startsWith('application/pdf');

  // Word. `.docx` is converted in the page; `.doc` cannot be, and says so
  // rather than leaving a blank frame nobody can explain.
  const isDocx = !servedPdf && isDocxShare(docMime, documentName) && !!playableDocUrl;
  const isLegacyDoc = !servedPdf && !isDocx && isLegacyDocShare(docMime, documentName);
  const { content: docxContent, loading: docxLoading, error: docxError } =
    useSharedDocx(playableDocUrl, isDocx);

  // Excel, on the same terms as Word: `.xlsx` is parsed in the page, `.xls`
  // cannot be and says so. Without this a spreadsheet fell through to the
  // "cannot be previewed in the browser" placeholder — a download link wearing
  // a document share's clothes.
  const isXlsx = !servedPdf && isXlsxShare(docMime, documentName) && !!playableDocUrl;
  const isLegacyXls = !servedPdf && !isXlsx && isLegacyXlsShare(docMime, documentName);
  const { sheets, loading: sheetLoading, error: sheetError } =
    useSharedSheet(playableDocUrl, isXlsx);
  // Which tab is open. Held by NAME, not index: a workbook that finishes
  // parsing after this was set would otherwise slide the viewer onto whichever
  // sheet inherited the number.
  const [openSheet, setOpenSheet] = useState<string | null>(null);
  const activeSheet =
    sheets?.find((s) => s.name === openSheet) ?? sheets?.[0] ?? null;

  /**
   * Can a browser actually display this, or would the iframe just download it?
   *
   * Office formats — doc, docx, xls, xlsx, ppt, pptx, all of which the share
   * panel accepts — render in no browser. Pointing an iframe at one starts a
   * download, and every participant runs this layout, so presenting a Word file
   * kicked off a download on everyone's machine instead of showing anything.
   *
   * An empty mime is a pass-through URL (a public link) that was never fetched,
   * so there is no type to test and the extension has to answer instead. It
   * cannot be waved through: an object served with `Content-Disposition:
   * attachment` — which is how the store keeps every non-renderable upload
   * safe — makes the iframe *save the file* rather than paint it, so simply
   * presenting a .docx started an unasked-for download on every participant's
   * machine. A link with no extension is still let through; that is a web page
   * or a viewer URL, and the iframe is exactly right for it.
   */
  //
  // The FILENAME decides, not the URL. Storage keys are `meetings/<room>/<uuid>`
  // with no extension, so testing the URL first meant `ext` was always
  // undefined for an uploaded file, `!ext` waved it through, and the iframe was
  // handed the very object this check exists to keep away from it.
  const extOf = (s: string | undefined) =>
    /\.([a-z0-9]{1,5})(?:[?#]|$)/i.exec(s ?? '')?.[1]?.toLowerCase();
  const ext = extOf(documentName) ?? extOf(documentUrl);

  // Only what the STORE serves inline. `attachment.service` keeps its public
  // bucket safe by storing everything outside its inline-safe list as
  // `application/octet-stream` with `Content-Disposition: attachment` — so for
  // any other type, embedding is not "preview vs no preview", it is a download
  // on every participant's machine. text/html and svg are excluded there
  // deliberately (both can execute), which is why they are excluded here.
  //
  // `.md` and `.log` are deliberately absent even though they are plain text:
  // browsers frequently upload them with no mime at all, the store cannot call
  // an unknown type inline-safe, and it stamps `attachment` on it — so claiming
  // they are embeddable is how a download gets triggered, not how it is avoided.
  const embeddableExt = new Set([
    'pdf',
    ...IMAGE_EXTENSIONS,
    'txt', 'csv', 'json',
  ]);
  const canEmbed =
    docMime === ''
      ? // No mime means a pass-through URL that was never fetched. An uploaded
        // share always has a filename to judge; only a bare link — a web page or
        // a viewer URL, which is exactly what an iframe is for — has neither.
        ext
        ? embeddableExt.has(ext)
        : !isUploadedShareUrl(documentUrl)
      : docMime.startsWith('application/pdf') ||
        // `text/` minus the executable ones. A blob: URL inherits this page's
        // origin, so framing HTML or an SVG served as text would run it as us.
        (docMime.startsWith('text/') && docMime !== 'text/html') ||
        (docMime.startsWith('image/') && docMime !== 'image/svg+xml');

  // Every PDF we uploaded is painted by us, on every device. The browser's own
  // viewer, in an iframe, keeps its page, scroll and zoom to itself, so nothing
  // drawn over it could stay on the page it was drawn on — and phones cannot
  // show a PDF in a frame at all (see needsPaintedPdf). A pasted link to
  // someone else's PDF stays framed where the browser can frame it: pdf.js
  // would need that server to allow CORS.
  const isPdf = docMime === '' ? ext === 'pdf' : docMime.startsWith('application/pdf');
  const [cannotFramePdf] = useState(needsPaintedPdf);
  const paintPdf = isPdf && (isUploadedShareUrl(documentUrl) || cannotFramePdf);

  // An image is shown as an image, fitted to the stage, rather than framed: a
  // frame paints it at its natural size in the corner, and gives no box that
  // matches the picture to draw over.
  const isImage = docMime === ''
    ? !!ext && IMAGE_EXTENSIONS.includes(ext)
    : docMime.startsWith('image/') && docMime !== 'image/svg+xml';

  // ── Annotation ──
  //
  // Only over what we paint ourselves — a PDF page or an image — because only
  // there do we know where the content sits. Word, Excel and text reflow to
  // each viewer's width, so ink would have nowhere stable to stay.
  const shareId = annotation ? annotation.shareId : null;
  const selfId = annotation?.selfId ?? null;
  const ink = useAnnotations({ doc: shareId, selfId, channel: annotation?.channel ?? null });
  const allowAnnotation = useMeetingHostControlsStore(selectCanAnnotate);
  const docKey = shareId ?? '';
  // Everyone sees the ink — on a phone too — once the room carries it.
  const showInk = !!shareId && !!selfId && ink.supported === true && !!playableDocUrl && (paintPdf || isImage);
  // Drawing, the same rule the server enforces: the host and co-hosts always;
  // while "Enable annotation" is on, also anyone granted annotation access.
  // Presenting the document grants nothing — the presenter asks like anyone.
  // Phones are view-only for now: they see the ink, with no toolbar or pen.
  const mayDraw =
    !!annotation?.isModerator || (allowAnnotation && !!annotation?.isAnnotator);
  const canDraw = showInk && !phoneLayout && mayDraw;
  // Everyone else gets a pen that asks the host — only while annotation is on.
  const canAskToDraw =
    showInk && !phoneLayout && !mayDraw && allowAnnotation && !!annotation?.onRequestAnnotation;
  // Select by default: the document scrolls until someone picks up a pen.
  const [tool, setTool] = useState<AnnotationTool>('select');
  const [shape, setShape] = useState<ShapeTool>('rect');
  const [stamp, setStamp] = useState<StampKind>('check');
  const [color, setColor] = useState('#ef4444');
  // Losing the right to draw puts the pen down, or the layer would keep
  // taking the pointer with no toolbar to change tools.
  const activeTool = canDraw ? tool : 'select';
  const strokes = useMeetingAnnotationStore((s) => s.strokes[docKey]) ?? NO_STROKES;
  const live = useMeetingAnnotationStore((s) => s.live[docKey]) ?? NO_LIVE;
  const canRedo = useMeetingAnnotationStore((s) => (s.undone[docKey]?.length ?? 0) > 0);
  const hasOwnStrokes = !!selfId && strokes.some((st) => st.by === selfId);

  const renderAnnotation = showInk && selfId
    ? (page: number, ratio: number) => (
        <AnnotationLayer
          page={page}
          ratio={ratio}
          strokes={strokes.filter((st) => st.page === page)}
          drafts={Object.values(live).filter((st) => st.page === page)}
          tool={activeTool}
          stamp={stamp}
          color={color}
          onStroke={(st) => ink.draw({ ...st, by: selfId })}
          onLive={ink.live}
          onErase={ink.erase}
        />
      )
    : undefined;

  // ── Asking for annotation access ──
  //
  // The pen opens the same ask-and-wait dialog as a presenter request. Approval
  // is the roles rebroadcast listing this client in `annotators`; a refusal is
  // `moderate:deny_annotator`, kept in the requests store.
  const [askOpen, setAskOpen] = useState(false);
  const deniedAnnotation = useAnnotationRequestsStore((s) => s.myOutcome === 'rejected');
  const setAnnotationOutcome = useAnnotationRequestsStore((s) => s.setMyOutcome);
  const isAnnotator = !!annotation?.isAnnotator;
  const askOutcome = isAnnotator ? 'approved' : deniedAnnotation ? 'rejected' : null;
  // The answer can land with the dialog closed; say so then, since nothing
  // else on screen would.
  // Not on a phone: phones are view-only (canDraw above), so a moderator's
  // grant there gives no pen, and "You can now annotate" promised one.
  const wasAnnotator = useRef(isAnnotator);
  useEffect(() => {
    if (isAnnotator && !wasAnnotator.current && !askOpen && showInk && !phoneLayout) {
      toast.success({ title: t("dialogs.canNowAnnotate") });
    }
    wasAnnotator.current = isAnnotator;
  }, [isAnnotator, askOpen, showInk, phoneLayout, t]);
  useEffect(() => {
    if (!deniedAnnotation || askOpen) return;
    // Which moderator said no, when there are several to ask.
    const name = useAnnotationRequestsStore.getState().myOutcomeBy?.name;
    toast.info({
      title: name
        ? t("dialogs.requestDeniedAnnotationBy", { name })
        : t("dialogs.requestDeniedByHostAnnotation"),
    });
    setAnnotationOutcome(null);
  }, [deniedAnnotation, askOpen, setAnnotationOutcome, t]);

  // ── Following the presenter ──
  //
  // Viewers are kept on the presenter's page until they scroll away by hand;
  // then a button takes them back. The presenter's own scrolling is what moves
  // everyone else.
  const followable = !!shareId && ink.supported === true && paintPdf;
  const [following, setFollowing] = useState(true);
  useEffect(() => setFollowing(true), [shareId]);


  return (
    <div className="flex h-full w-full min-w-0 overflow-hidden">
      {/* Main Content Area - PDF Viewer */}
      <div ref={mainRef} className="relative min-h-0 min-w-0 flex-1 pr-2 bg-vc-surface">
        {/* Top Banner */}
        {/* z-25: must outrank the document iframe below. The iframe lives in a
            `position: relative; z-index: auto` container, which creates no
            stacking context, so its z-10 competed directly with this bar's —
            and being later in DOM order it won, hiding the close button.
            No higher than 25: the same holds for the meeting's own overlays,
            so the request chip and the host's requests pill (z-26, top right
            of the video area) would land under this bar. At z-30 they did. */}
        {/* The title side shrinks and truncates; the buttons never do. In full
            screen the mic and camera controls join the buttons, and on a phone
            the untruncated title then pushed exit and close off the right edge,
            where nobody could reach them. Clear of the notch while full screen,
            as screen share's controls are. */}
        <div
          className="absolute top-0 left-0 right-0 z-[25] flex items-center justify-between gap-2 px-4 py-2 bg-slate-800/80 backdrop-blur-sm border-b border-slate-700"
          style={isFullscreen ? {
            paddingTop: "max(8px, env(safe-area-inset-top))",
            paddingLeft: "max(16px, env(safe-area-inset-left))",
            paddingRight: "max(16px, env(safe-area-inset-right))",
          } : undefined}
        >
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="flex min-w-0 items-center gap-2 px-3 py-1.5 bg-slate-700/50 rounded-full">
              <span className="w-2 h-2 shrink-0 bg-red-500 rounded-full animate-pulse" />
              <span className="truncate text-sm text-white font-medium">
                {isPresenter
                  ? t("layouts.youAreSharingFile")
                  : t("layouts.isSharingFile", { name: presenterName })}
              </span>
            </div>
            <span className="min-w-0 truncate text-sm text-slate-300 dark:text-muted-foreground">{documentName}</span>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {isFullscreen && fullscreenControls && (
              <div className="mr-2 flex shrink-0 items-center gap-2">{fullscreenControls}</div>
            )}
            <button
              onClick={toggleFullscreen}
              className="p-2 cursor-pointer text-white hover:bg-slate-700 rounded-lg transition"
              title={isFullscreen ? t("layouts.exitFullscreen") : t("layouts.enterFullscreen")}
              aria-label={isFullscreen ? t("layouts.exitFullscreen") : t("layouts.enterFullscreen")}
              aria-pressed={isFullscreen}
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            {canClose && (
              <button
                onClick={handleClose}
                className="p-2 cursor-pointer text-white hover:bg-slate-700 rounded-lg transition"
                title={t("layouts.stopPresenting")}
                aria-label={t("layouts.stopPresenting")}
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {/* PDF Container - Added relative positioning for toolbar */}
        <div className="relative h-full w-full overflow-hidden rounded-[14px] ring-1 ring-white/10 bg-vc-surface">
          {/* Annotation Toolbar - positioned absolute within container */}
          {canDraw && (
            <div className="absolute left-4 top-20 z-20">
              <AnnotationToolbar
                tool={tool}
                onToolChange={setTool}
                shape={shape}
                onShapeChange={setShape}
                stamp={stamp}
                onStampChange={setStamp}
                color={color}
                onColorChange={setColor}
                onUndo={ink.undo}
                onRedo={ink.redo}
                onClear={ink.clearMine}
                onClearAll={ink.clearAll}
                canUndo={hasOwnStrokes}
                canRedo={canRedo}
                canClear={hasOwnStrokes}
                canClearAll={!!annotation?.isModerator}
              />
            </div>
          )}

          {/* Where the toolbar would be: a pen that asks for access. */}
          {canAskToDraw && (
            <div className="absolute left-4 top-20 z-20">
              <button
                type="button"
                onClick={() => {
                  setAnnotationOutcome(null);
                  setAskOpen(true);
                }}
                title={t("dialogs.requestAnnotationAccess")}
                aria-label={t("dialogs.requestAnnotationAccess")}
                className="cursor-pointer rounded-2xl border border-vc-panel-border bg-card/95 p-2.5 shadow-lg backdrop-blur-sm transition hover:bg-card"
              >
                <PenLine className="h-5 w-5 text-slate-600 dark:text-muted-foreground" />
              </button>
            </div>
          )}

          {/* Back to the presenter, once a viewer has scrolled away from them. */}
          {followable && !isPresenter && !following && ink.view && (
            <button
              type="button"
              onClick={() => setFollowing(true)}
              className="absolute right-3 top-16 z-20 cursor-pointer rounded-full bg-black/70 px-3 py-1.5 text-xs font-semibold text-white ring-1 ring-white/20 transition hover:bg-black/80"
            >
              {t("layouts.backToPresenter")}
            </button>
          )}

          {/* The shared document. z-10 keeps it under the annotation toolbar
              (z-20) and the header (z-25). A PDF brings its own viewer, with
              its own paging and zoom — the controls that used to sit here drove
              a hardcoded five-page mock and moved counters nothing read. */}
          {docResolving ? (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/90">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
              <p className="text-sm text-slate-600">Loading shared document…</p>
            </div>
          ) : docError ? (
            /* A share that could not be fetched offers the download as a
               CHOICE. It used to fall back to framing the store URL, which the
               store answers with `Content-Disposition: attachment` — so the
               failure mode was a file saved on every participant's machine and
               a blank frame, a download nobody asked for. */
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/90 px-8 text-center">
              <FileText className="h-10 w-10 text-slate-300" />
              <p className="text-base font-medium text-slate-700">{documentName}</p>
              <p className="max-w-sm text-sm text-slate-500">{docError}</p>
              {documentUrl && (
                <button
                  type="button"
                  onClick={() => void downloadSharedFile(documentUrl, documentName)}
                  className="mt-1 rounded-lg bg-slate-700 px-3 py-1.5 text-sm text-white hover:bg-slate-600"
                >
                  {t("panels.download")}
                </button>
              )}
            </div>
          ) : isDocx ? (
            <div className="absolute inset-x-0 top-14 bottom-0 z-10 overflow-auto bg-white px-6 py-5">
              {docxLoading ? (
                <p className="text-sm text-slate-500">Opening document…</p>
              ) : docxError ? (
                <p className="text-sm text-slate-700">{docxError}</p>
              ) : (
                <div className="docx-body mx-auto max-w-3xl text-[14px] leading-relaxed text-slate-800 [&_h1]:mb-2 [&_h1]:mt-4 [&_h1]:text-2xl [&_h1]:font-semibold [&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:text-xl [&_h2]:font-semibold [&_h3]:mb-1 [&_h3]:mt-3 [&_h3]:text-lg [&_h3]:font-semibold [&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_table]:my-3 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-slate-200 [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-slate-200 [&_th]:bg-slate-100 [&_th]:px-2 [&_th]:py-1 [&_blockquote]:my-2 [&_blockquote]:border-l-4 [&_blockquote]:border-slate-200 [&_blockquote]:pl-3 [&_blockquote]:text-slate-600">
                  {docxContent}
                </div>
              )}
            </div>
          ) : isXlsx ? (
            <div className="absolute inset-x-0 top-14 bottom-0 z-10 flex flex-col overflow-hidden bg-white">
              {sheetLoading ? (
                <p className="p-4 text-sm text-slate-500">Opening spreadsheet…</p>
              ) : sheetError ? (
                <p className="p-4 text-sm text-slate-700">{sheetError}</p>
              ) : !activeSheet ? (
                <p className="p-4 text-sm text-slate-500">This workbook has no sheets.</p>
              ) : (
                <>
                  <div className="min-h-0 flex-1 overflow-auto p-4">
                    {activeSheet.rows.length === 0 ? (
                      <p className="text-sm text-slate-500">This sheet is empty.</p>
                    ) : (
                      <table className="min-w-full border-collapse text-left text-[13px] text-slate-800">
                        <tbody>
                          {activeSheet.rows.map((row, r) => (
                            <tr key={r} className={r === 0 ? "bg-slate-100 font-semibold" : r % 2 ? "bg-slate-50" : ""}>
                              {row.map((cell, c) => (
                                <td key={c} className="border border-slate-200 px-2 py-1 align-top whitespace-pre-wrap">
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {activeSheet.truncated && (
                      <p className="mt-3 text-xs text-slate-500">
                        Showing the first {activeSheet.rows.length} of {activeSheet.totalRows} rows.
                        Download the file for the rest.
                      </p>
                    )}
                  </div>

                  {/* Tabs only when there is a choice to make — a one-sheet
                      workbook is the common case and gains nothing from a bar
                      holding a single button. */}
                  {sheets && sheets.length > 1 && (
                    <div className="flex shrink-0 items-center gap-1 overflow-x-auto border-t border-slate-200 bg-slate-50 px-2 py-1.5">
                      {sheets.map((sh) => (
                        <button
                          key={sh.name}
                          type="button"
                          onClick={() => setOpenSheet(sh.name)}
                          aria-pressed={sh.name === activeSheet.name}
                          title={sh.name}
                          className={[
                            "shrink-0 rounded px-3 py-1 text-xs font-medium whitespace-nowrap",
                            sh.name === activeSheet.name
                              ? "bg-white text-slate-900 ring-1 ring-slate-300"
                              : "text-slate-600 hover:bg-slate-200",
                          ].join(" ")}
                        >
                          <span className="max-w-[12rem] truncate inline-block align-bottom">{sh.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : isLegacyXls ? (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/90 px-8 text-center">
              <FileText className="h-10 w-10 text-slate-300" />
              <p className="text-base font-medium text-slate-700">{documentName}</p>
              <p className="max-w-sm text-sm text-slate-500">
                This is a legacy Excel (.xls) file, which browsers cannot open. Save it as .xlsx
                and share again, or download it below.
              </p>
              {documentUrl && (
                <button
                  type="button"
                  onClick={() => void downloadSharedFile(documentUrl, documentName)}
                  className="mt-1 rounded-lg bg-slate-700 px-3 py-1.5 text-sm text-white hover:bg-slate-600"
                >
                  {t("panels.download")}
                </button>
              )}
            </div>
          ) : isLegacyDoc ? (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/90 px-8 text-center">
              <FileText className="h-10 w-10 text-slate-300" />
              <p className="text-base font-medium text-slate-700">{documentName}</p>
              <p className="max-w-sm text-sm text-slate-500">
                This is a legacy Word (.doc) file, which browsers cannot open. Save it as .docx
                and share again, or download it below.
              </p>
              {documentUrl && (
                <button
                  type="button"
                  onClick={() => void downloadSharedFile(documentUrl, documentName)}
                  className="mt-1 rounded-lg bg-slate-700 px-3 py-1.5 text-sm text-white hover:bg-slate-600"
                >
                  {t("panels.download")}
                </button>
              )}
            </div>
          ) : showAsText ? (
            <div className="absolute inset-x-0 top-14 bottom-0 z-10 overflow-auto bg-white p-4">
              {textLoading ? (
                <p className="text-sm text-slate-500">Loading shared document…</p>
              ) : csv ? (
                <table className="min-w-full border-collapse text-left text-[13px] text-slate-800">
                  <tbody>
                    {csv.rows.map((row, r) => (
                      <tr key={r} className={r === 0 ? "bg-slate-100 font-semibold" : r % 2 ? "bg-slate-50" : ""}>
                        {row.map((cell, c) => (
                          <td key={c} className="border border-slate-200 px-2 py-1 align-top whitespace-pre-wrap">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <pre className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-slate-800">
                  {sharedText}
                </pre>
              )}
              {csv?.truncated && (
                <p className="mt-3 text-xs text-slate-500">
                  Showing the first {csv.rows.length} rows. Download the file for the rest.
                </p>
              )}
            </div>
          ) : playableDocUrl && paintPdf ? (
            <div className="absolute inset-x-0 top-14 bottom-0 z-10">
              <SharedPdfViewer
                url={playableDocUrl}
                name={documentName}
                // A text file is shown as its PDF copy, small type on a whole
                // page: it opens fitted to the width, where it reads, and the
                // room follows the presenter down it.
                fit={ext === 'txt' ? 'width' : 'page'}
                renderPageOverlay={renderAnnotation}
                onViewChange={followable && isPresenter ? ink.sendView : undefined}
                followView={followable && !isPresenter && following ? ink.view : null}
                onUserScroll={followable && !isPresenter ? () => setFollowing(false) : undefined}
              />
            </div>
          ) : playableDocUrl && isImage ? (
            <div className="absolute inset-x-0 top-14 bottom-0 z-10">
              <SharedImageViewer
                key={playableDocUrl}
                url={playableDocUrl}
                name={documentName}
                renderOverlay={renderAnnotation}
              />
            </div>
          ) : playableDocUrl && canEmbed ? (
            <iframe
              src={playableDocUrl}
              title={documentName}
              // `top-14` rather than `inset-0 pt-14`: padding still leaves the
              // element's box over the header, so the iframe swallowed clicks
              // on the close button even once it painted underneath.
              //
              // Height is stated outright rather than inferred from top+bottom.
              // An iframe is a replaced element: with no resolved height it
              // falls back to its intrinsic 150px-ish default, which is what
              // left the document in a band at the top with the container's
              // background filling everything below it.
              className="absolute inset-x-0 top-14 z-10 w-full bg-white"
              style={{ border: 0, height: 'calc(100% - 3.5rem)' }}
              referrerPolicy="strict-origin-when-cross-origin"
            />
          ) : null}

          {/* Shown only when there is no document to point at. This used to
              render a hardcoded "User Story Specification" — title, version,
              table, five pages of prose — beneath the iframe, so any file the
              iframe could not paint left the presenter and every viewer looking
              at a convincing document that was not theirs. A placeholder must
              never look like content. */}
          {!docResolving && !docError && !showAsText && !isDocx && !isLegacyDoc && !isXlsx && !isLegacyXls && (!playableDocUrl || !canEmbed) && (
            <div className="absolute inset-0 flex items-center justify-center p-8 pt-20">
              <div className="flex flex-col items-center gap-3 text-center">
                <FileText className="h-12 w-12 text-slate-300 dark:text-muted-foreground" />
                <p className="text-base font-medium text-slate-700 dark:text-foreground">
                  {documentName}
                </p>
                <p className="max-w-sm text-sm text-slate-500 dark:text-muted-foreground">
                  {playableDocUrl
                    ? 'This file type cannot be previewed in the browser. Everyone can download it from the Documents panel.'
                    : 'This document has no preview available to the room.'}
                </p>
              </div>
            </div>
          )}

          <ScreenShareRequestDialog
            open={askOpen}
            onOpenChange={(open) => {
              setAskOpen(open);
              if (!open) setAnnotationOutcome(null);
            }}
            subject="annotation"
            outcome={askOutcome}
            onRequest={() => annotation?.onRequestAnnotation?.()}
            // The toolbar is already open by now; start them with a pen in hand.
            onApproved={() => setTool('pen')}
            onRejected={() => setAnnotationOutcome(null)}
          />

          {/* Presenter Info Badge */}
          <div className="absolute left-3 bottom-3 rounded-full bg-black/45 px-3 py-1 text-xs font-semibold text-white/80 ring-1 ring-white/10">
            {presenterName} ({isPresenter ? t("layouts.youPresenting") : t("layouts.presenting")})
          </div>
        </div>
      </div>

      {!phoneLayout && (<>
      {/* Resizer Handle */}
      <div
        role="separator"
        aria-orientation="vertical"
        onPointerDown={(e) => {
          if (railDragRef) {
            railDragRef.current = { startX: e.clientX, startWidth: rightRailWidth }
          }
        }}
        className="relative mr-2 hidden w-3 cursor-col-resize select-none items-center justify-center rounded-full bg-card/5 hover:bg-card/10 lg:flex"
        title={t("layouts.resize")}
      >
        <div className="grid gap-1">
          <span className="h-1 w-1 rounded-full bg-card/40" />
          <span className="h-1 w-1 rounded-full bg-card/40" />
          <span className="h-1 w-1 rounded-full bg-card/40" />
        </div>
      </div>

      {/* Right Sidebar - Participant Tiles */}
      <aside className="min-h-0 shrink-0 overflow-hidden bg-transparent" style={{ width: rightRailWidth }}>
        <div ref={(node) => setRightRailEl(node)} className="relative h-full min-h-0 overflow-hidden">
          {rightRailPageCount > 1 ? (
            <>
              <button
                type="button"
                onClick={() => onSetRightRailPage((p) => Math.max(0, p - 1))}
                disabled={safeRightRailPage <= 0}
                className="absolute cursor-pointer left-1/2 top-2 z-10 grid h-9 w-9 -translate-x-1/2 place-items-center rounded-full bg-black/45 text-white/80 ring-1 ring-white/10 transition hover:bg-black/55 hover:text-white disabled:opacity-30"
                title={t("layouts.previous")}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M18 15l-6-6-6 6" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => onSetRightRailPage((p) => Math.min(rightRailPageCount - 1, p + 1))}
                disabled={safeRightRailPage >= rightRailPageCount - 1}
                className="absolute cursor-pointer left-1/2 bottom-2 z-10 grid h-9 w-9 -translate-x-1/2 place-items-center rounded-full bg-black/45 text-white/80 ring-1 ring-white/10 transition hover:bg-black/55 hover:text-white disabled:opacity-30"
                title={t("layouts.next")}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 9l6 6-6-6" />
                </svg>
              </button>
              <div className="absolute right-2 top-2 z-10 rounded-full bg-black/35 px-2 py-1 text-[11px] font-semibold text-white/70 ring-1 ring-white/10 tabular-nums">
                {safeRightRailPage + 1}/{rightRailPageCount}
              </div>
            </>
          ) : null}

          {railRowChunks.length === 0 ? (
            <div className="flex items-center justify-center h-full text-white/60 text-sm">
              {t("layouts.noParticipants")}
            </div>
          ) : (
            <RailTiles
              rows={railRowChunks}
              rowHeight={railRowH}
              onSelect={onPin}
              renderTile={(p) => (
                <VideoTile
                  participant={p}
                  reaction={reactionFor(p?.id)}
                  isPinned={pinnedIds.includes(p.id)}
                  onPin={onPin}
                  onPinForAll={onPinForAll}
                  pinnedForAllId={pinnedForAllId}
                  onMaximize={onMaximize}
                  onOpenChat={onOpenChat}
                  virtualBackgroundUrl={p.isLocal ? selectedBackground?.url ?? null : null}
                />
              )}
            />
          )}
        </div>
      </aside>
      </>)}
    </div>
  );
}
